import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { Plus, ListTodo, Trash2, Circle, CircleDot, CheckCircle2 } from "lucide-react";

const STATUSES = [
  { key: "todo", label: "To do", icon: Circle, color: "text-[#525252]" },
  { key: "doing", label: "Doing", icon: CircleDot, color: "text-[#f4b847]" },
  { key: "done", label: "Done", icon: CheckCircle2, color: "text-[#25AAAA]" },
];

export default function Tasks() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [tasks, setTasks] = useState([]);
  const [team, setTeam] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", notes: "", assignee_user_id: "", status: "todo", due_at: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [t, teamRes] = await Promise.all([
      api.get("/team-tasks").then((r) => r.data).catch(() => []),
      api.get("/teams/my").then((r) => r.data).catch(() => ({ team: null, members: [] })),
    ]);
    setTasks(t || []);
    setTeam(teamRes || { team: null, members: [] });
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      const assignee = (team?.members || []).find((m) => m.id === form.assignee_user_id);
      await api.post("/team-tasks", {
        title: form.title.trim(),
        notes: form.notes.trim(),
        assignee_user_id: form.assignee_user_id || null,
        assignee_name: assignee?.name || null,
        status: form.status,
        due_at: form.due_at || null,
      });
      toast.success("Task added");
      setOpen(false);
      setForm({ title: "", notes: "", assignee_user_id: "", status: "todo", due_at: "" });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const setStatus = async (t, status) => {
    await api.put(`/team-tasks/${t.id}`, { status });
    await load();
  };

  const remove = async (t) => {
    if (!(await confirm({ title: "Delete this task?", destructive: true }))) return;
    await api.delete(`/team-tasks/${t.id}`);
    await load();
  };

  if (user?.role === "student" && !team?.team) {
    return (
      <div className="border-2 border-dashed border-[#f4b847] bg-[#FEF3D0] p-8" data-testid="tasks-no-team">
        <h2 className="font-display text-xl font-bold text-[#7A5B17]">Join a team first</h2>
        <p className="text-sm text-[#7A5B17] mt-2">Tasks are shared within your team. Head to Sign-in &rarr; Enter join code.</p>
      </div>
    );
  }

  const byStatus = STATUSES.map((s) => ({ ...s, tasks: tasks.filter((t) => (t.status || "todo") === s.key) }));

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-3xl font-black tracking-tight text-[#0F1F2E]">Team tasks</h2>
          <p className="text-sm text-[#525252] mt-1">Keep track of who's doing what. Anyone on your team can add or update tasks.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-[#25AAAA] hover:bg-[#1e7d7d] rounded-none" data-testid="task-new-btn">
          <Plus size={16} className="mr-2" /> Add task
        </Button>
      </div>

      {tasks.length === 0 ? (
        <div className="border-2 border-dashed border-[#E5E7EB] bg-white p-10 text-center" data-testid="tasks-empty">
          <ListTodo size={32} className="mx-auto text-[#525252]" />
          <p className="text-sm text-[#525252] mt-3">No tasks yet — start capturing what your team needs to do.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {byStatus.map((col) => {
            const ColIcon = col.icon;
            return (
              <div key={col.key} className="border border-[#E5E7EB] bg-white p-4" data-testid={`tasks-col-${col.key}`}>
                <div className="flex items-center gap-2 mb-3">
                  <ColIcon size={16} className={col.color}/>
                  <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252]">{col.label}</div>
                  <span className="ml-auto text-xs text-[#525252]">{col.tasks.length}</span>
                </div>
                <div className="space-y-2">
                  {col.tasks.map((t) => (
                    <div key={t.id} className="border border-[#E5E7EB] bg-[#FDFDFD] p-3" data-testid={`task-${t.id}`}>
                      <div className="text-sm font-semibold text-[#0F1F2E]">{t.title}</div>
                      {t.notes && <div className="text-xs text-[#525252] mt-1 whitespace-pre-wrap">{t.notes}</div>}
                      {t.assignee_name && (
                        <div className="text-[10px] uppercase tracking-wider text-[#25AAAA] mt-2 font-semibold">→ {t.assignee_name}</div>
                      )}
                      <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                        {STATUSES.filter((s) => s.key !== col.key).map((s) => (
                          <button key={s.key} onClick={() => setStatus(t, s.key)}
                            className="text-[10px] uppercase tracking-wider px-2 py-1 border border-[#E5E7EB] hover:border-[#25AAAA] hover:text-[#25AAAA]"
                            data-testid={`task-${t.id}-to-${s.key}`}>
                            → {s.label}
                          </button>
                        ))}
                        <button onClick={() => remove(t)} className="text-[10px] uppercase tracking-wider text-[#E93E36] hover:underline ml-auto" data-testid={`task-${t.id}-delete`}>
                          <Trash2 size={10} className="inline mr-1"/>Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="task-dialog">
          <DialogHeader><DialogTitle>Add a task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-input-title"/>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="task-input-notes"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Assign to</Label>
                <Select value={form.assignee_user_id} onValueChange={(v) => setForm({ ...form, assignee_user_id: v })}>
                  <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="No one" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">No one</SelectItem>
                    {(team?.members || []).map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="task-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
            <Button disabled={busy} className="bg-[#25AAAA] hover:bg-[#1e7d7d] rounded-none" onClick={create} data-testid="task-save-btn">
              {busy ? "Adding…" : "Add task"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
